import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { useWarehouseStore } from '@/stores/warehouseStore';
import {
  createProductionEntry,
  getAllProductionEntries,
  updateProductionEntry,
  deleteProductionEntry,
  approveProductionEntry,
} from '@/api/productionentry.api';
import { getAllProductionOrders, getMaterialReservations, getFullProductionOrderDetails } from '@/api/productionOrder.api';
import { isReworkWorkOrder } from '@/utils/stageProgress';
import { getAllRoutings } from '@/api/routing.api';
import { getAllShifts } from '@/api/shift.api';
import { getAllOperators } from '@/api/operators.api';
import { getAllMachines } from '@/api/machine.api';
import { getAllRejectionCodes } from '@/api/rejectioncode.api';
import { getAllDowntimeCodes } from '@/api/downtimecode.api';
import { createDowntimeEntry, resolveDowntimeEntry } from '@/api/downtime.api';
import { toInputDate, toLocalDate } from '@/utils/format';
import { getAllWorkCenters } from '@/api/workcenter.api';
import { getAllWorkOrders } from '@/api/workorder.api';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

import { getBatchSuggestions } from '@/services/traceabilityService';
import { useUnits } from '@/hooks/useUnits';
import { useWorkCenters } from '@/hooks/useWorkCenters';

interface FormState {
  productionOrderId: string;
  workOrderId: string;
  date: string;
  shiftId: string;
  operatorId: string;
  machineId: string;
  startTime: string;
  endTime: string;
  producedQty: string;
  rejectedQty: string;
  rejectionCodeId: string;
  downtimeCodeId: string;
  notes: string;
  rawMaterialBatchNumber: string;
  outputBatchNumber: string;
}

const emptyForm = (): FormState => ({
  productionOrderId: '',
  workOrderId: '',
  date: toInputDate(new Date()),
  shiftId: '',
  operatorId: '',
  machineId: '',
  startTime: '',
  endTime: '',
  producedQty: '',
  rejectedQty: '',
  rejectionCodeId: '',
  downtimeCodeId: '',
  notes: '',
  rawMaterialBatchNumber: '',
  outputBatchNumber: '',
});

function timeToMinutes(t: string): number {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

// Shop floor rows that are activity logs, not production output
const NON_PRODUCTION_STAGES = ['MATERIAL_ISSUE', 'Material Issue', 'DOWNTIME', 'Downtime', 'QC_INSPECTION', 'FINAL_COMPLETION'];

/**
 * Activity rows the system writes (material issue, work order start, QC result, completion):
 * a non-production stage, or nothing produced or rejected — a real entry always carries a quantity.
 */
const isActivityLog = (e: { stageName: string; producedQty: number; rejectedQty: number }) =>
  NON_PRODUCTION_STAGES.includes(e.stageName) || (Number(e.producedQty) === 0 && Number(e.rejectedQty) === 0);

const mapResponseToEntry = (r: any) => ({
  id: r.id,
  entryNumber: r.entry_number ?? r.entryNumber ?? '',
  productionOrderId: r.production_order_id ?? r.productionOrderId ?? '',
  productionOrderNumber: r.production_order_number ?? r.productionOrderNumber ?? '',
  workOrderId: r.work_order_id ?? r.workOrderId ?? '',
  workOrderNumber: r.work_order_number ?? r.workOrderNumber ?? '',
  stageName: r.stage_name ?? r.stageName ?? '',
  stageNumber: r.stage_number ?? r.stageNumber ?? null,
  workCenterId: r.work_center_id ?? r.workCenterId ?? '',
  workCenterName: r.work_center_name ?? r.workCenterName ?? '',
  machineId: r.machine_id ?? r.machineId ?? '',
  machineName: r.machine_name ?? r.machineName ?? '',
  operatorId: r.operator_id ?? r.operatorId ?? '',
  operatorName: r.operator_name ?? r.operatorName ?? '',
  shiftId: r.shift_id ?? r.shiftId ?? '',
  shiftName: r.shift_name ?? r.shiftName ?? '',
  date: toLocalDate(r.date),
  producedQty: Number(r.produced_qty ?? r.producedQty ?? 0),
  rejectedQty: Number(r.rejected_qty ?? r.rejectedQty ?? 0),
  unit: r.unit ?? 'Pcs',
  startTime: r.start_time ?? r.startTime ?? '',
  endTime: r.end_time ?? r.endTime ?? '',
  actualTimeMinutes: Number(r.actual_time_minutes ?? r.actualTimeMinutes ?? 0),
  rejectionCodeId: r.rejection_code_id ?? r.rejectionCodeId ?? '',
  notes: r.notes ?? '',
  status: r.status ?? 'PENDING',
  isApproved: r.status === 'APPROVED',
  createdAt: r.created_at ?? r.createdAt ?? '',
  warehouseId: r.warehouse_id ?? r.warehouseId ?? '',
  batchNumber: r.batch_number ?? r.batchNumber ?? r.output_batch_number ?? '',
});

export default function ProductionEntryPage() {
  // Work center shown as "WC-001 · Assembly Line"
  const workCenterLookup = useWorkCenters();
  const units = useUnits();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedWarehouseId } = useWarehouseStore();
  // Where to go back to after saving an entry opened from another page (e.g. Complete Work Order)
  const [returnUrl, setReturnUrl] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [poFilter, setPoFilter] = useState('ALL');
  const [wcFilter, setWcFilter] = useState('ALL');
  const [opFilter, setOpFilter] = useState('ALL');
  const [approvedFilter, setApprovedFilter] = useState<'ALL' | 'APPROVED' | 'PENDING'>('ALL');
  const [showSlideOver, setShowSlideOver] = useState(false);
  const [entryType, setEntryType] = useState<'MATERIAL_ISSUE' | 'PRODUCTION_ENTRY' | 'DOWNTIME' | 'REJECTION'>('PRODUCTION_ENTRY');
  const [sfMaterialItems, setSfMaterialItems] = useState<any[]>([]);
  const [sfMaterialsLoading, setSfMaterialsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);
  // Units the selected work order may still record, given the material issued to its production order
  const [materialLimit, setMaterialLimit] = useState<{ max: number | null; allowed: number; limiting: string; prevStage: string; prevGood: number | null } | null>(null);
  const [activeTab, setActiveTab] = useState<'entries' | 'rejections'>('entries');

  // Rejection form
  const [showRejectionSlideOver, setShowRejectionSlideOver] = useState(false);
  const [rejectionForm, setRejectionForm] = useState({
    productionOrderId: '',
    workOrderId: '',
    date: toInputDate(new Date()),
    shiftId: '',
    operatorId: '',
    machineId: '',
    rejectionCodeId: '',
    rejectedQty: '',
    notes: '',
  });
  const [rejectionError, setRejectionError] = useState<string | null>(null);

  // Real API Data States
  const [entries, setEntries] = useState<any[]>([]);
  const [productionOrders, setProductionOrders] = useState<any[]>([]);
  const [routings, setRoutings] = useState<any[]>([]);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [operators, setOperators] = useState<any[]>([]);
  const [machines, setMachines] = useState<any[]>([]);
  const [rejectionCodes, setRejectionCodes] = useState<any[]>([]);
  const [downtimeCodes, setDowntimeCodes] = useState<any[]>([]);
  const [workCenters, setWorkCenters] = useState<any[]>([]);
  const [availableRmBatches, setAvailableRmBatches] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [
        entriesRes,
        poRes,
        routingsRes,
        workOrdersRes,
        shiftsRes,
        operatorsRes,
        machinesRes,
        rejRes,
        wcRes,
        rmBatchesRes,
        downtimeCodesRes,
      ] = await Promise.all([
        getAllProductionEntries(selectedWarehouseId || undefined),
        getAllProductionOrders({ warehouseId: selectedWarehouseId || undefined }),
        getAllRoutings(selectedWarehouseId || undefined),
        getAllWorkOrders(selectedWarehouseId),
        // Shifts and operators without a warehouse are shared, so load all and filter on the page
        getAllShifts(),
        getAllOperators(),
        getAllMachines(selectedWarehouseId || undefined),
        getAllRejectionCodes(selectedWarehouseId || undefined),
        getAllWorkCenters(selectedWarehouseId || undefined),
        getBatchSuggestions('forward', '', selectedWarehouseId || undefined).catch(() => ({ success: false, data: [] })),
        getAllDowntimeCodes().catch(() => ({ success: false, data: [] as any[] })),
      ]);
      if (downtimeCodesRes.success && Array.isArray(downtimeCodesRes.data)) {
        setDowntimeCodes(
          downtimeCodesRes.data.filter(
            (c: any) => c.is_active !== false && (!c.warehouse_id || !selectedWarehouseId || c.warehouse_id === selectedWarehouseId),
          ),
        );
      }

      if (entriesRes.success && entriesRes.data) {
        setEntries(entriesRes.data.map(mapResponseToEntry));
      }
      if (poRes.success && poRes.data) {
        setProductionOrders(poRes.data);
      }
      if (routingsRes.success && routingsRes.data) {
        setRoutings(routingsRes.data);
      }
      if (workOrdersRes.success && workOrdersRes.data) {
        setWorkOrders(workOrdersRes.data);
      }
      if (shiftsRes.success && shiftsRes.data) {
        setShifts(shiftsRes.data);
      }
      if (operatorsRes.success && operatorsRes.data) {
        setOperators(operatorsRes.data);
      }
      if (machinesRes.success && machinesRes.data) {
        setMachines(machinesRes.data);
      }
      if (rejRes.success && rejRes.data) {
        setRejectionCodes(rejRes.data);
      }
      if (wcRes.success && wcRes.data) {
        setWorkCenters(wcRes.data);
      }
      if (rmBatchesRes.success && Array.isArray(rmBatchesRes.data)) {
        setAvailableRmBatches(rmBatchesRes.data);
      }
    } catch (error) {
      console.error('Failed to load production entries:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to load page data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedWarehouseId]);

  // Warehouse partitioning filters
  // POs and entries are warehouse-scoped; masters (shifts/operators/machines/workcenters) are global
  const warehousePOs = useMemo(() => {
    if (!selectedWarehouseId) return productionOrders;
    return productionOrders.filter(
      (po) => po.warehouse_id === selectedWarehouseId || po.warehouseId === selectedWarehouseId
    );
  }, [productionOrders, selectedWarehouseId]);

  // Warehouse-scoped masters
  const warehouseShifts = useMemo(() => {
    if (!selectedWarehouseId) return shifts;
    return shifts.filter((s) => !s.warehouse_id || s.warehouse_id === selectedWarehouseId);
  }, [shifts, selectedWarehouseId]);

  const warehouseOperators = useMemo(() => {
    if (!selectedWarehouseId) return operators;
    return operators.filter((o) => !o.warehouse_id || o.warehouse_id === selectedWarehouseId);
  }, [operators, selectedWarehouseId]);

  const warehouseMachines = useMemo(() => {
    if (!selectedWarehouseId) return machines;
    return machines.filter((m) => m.warehouse_id === selectedWarehouseId || m.warehouseId === selectedWarehouseId);
  }, [machines, selectedWarehouseId]);

  const warehouseRejectionCodes = useMemo(() => {
    if (!selectedWarehouseId) return rejectionCodes;
    return rejectionCodes.filter((rc) => !rc.warehouse_id || rc.warehouse_id === selectedWarehouseId);
  }, [rejectionCodes, selectedWarehouseId]);

  const warehouseWorkCenters = useMemo(() => {
    if (!selectedWarehouseId) return workCenters;
    return workCenters.filter((wc) => !wc.warehouse_id || wc.warehouse_id === selectedWarehouseId);
  }, [workCenters, selectedWarehouseId]);

  const warehouseEntries = useMemo(() => {
    if (!selectedWarehouseId) return entries;
    return entries.filter(
      (e) => e.warehouseId === selectedWarehouseId
    );
  }, [entries, selectedWarehouseId]);

  // Work Orders filtered by selected PO for Entry Form
  const selectedPO = useMemo(() => {
    return warehousePOs.find((po) => po.id === form.productionOrderId) || null;
  }, [form.productionOrderId, warehousePOs]);

  // Use real work orders from DB, filtered by selected production order
  const selectedPOWorkOrders = useMemo(() => {
    if (!form.productionOrderId) return [];
    return workOrders.filter(
      (wo) => wo.production_order_id === form.productionOrderId
    );
  }, [form.productionOrderId, workOrders]);

  // Keep routing stages as fallback if no work orders exist
  const selectedPOStages = useMemo(() => {
    if (selectedPOWorkOrders.length > 0) return []; // prefer work orders
    if (!selectedPO || !selectedPO.routing_id) return [];
    const routing = routings.find((r) => r.id === selectedPO.routing_id);
    if (!routing) return [];
    try {
      const parsed = typeof routing.stages === 'string' ? JSON.parse(routing.stages) : routing.stages;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Failed to parse routing stages:', e);
      return [];
    }
  }, [selectedPO, routings, selectedPOWorkOrders]);

  // Selected work order (from DB) or routing stage (fallback)
  const selectedWorkOrder = useMemo(() => {
    return selectedPOWorkOrders.find((wo) => wo.id === form.workOrderId) || null;
  }, [form.workOrderId, selectedPOWorkOrders]);

  const selectedStage = useMemo(() => {
    const stageNum = Number(form.workOrderId);
    return selectedPOStages.find((s) => s.sequence === stageNum) || null;
  }, [form.workOrderId, selectedPOStages]);

  // Machines filtered by the work center of the selected work order
  const availableMachines = useMemo(() => {
    const wcId = selectedWorkOrder?.work_center_id || selectedStage?.workCenterId || selectedStage?.work_center_id;
    const wcName = String(selectedWorkOrder?.work_center_name || selectedStage?.workCenterName || '').trim().toLowerCase();
    if (!wcId && !wcName) return [];
    return warehouseMachines.filter(
      (m) => m.work_center_id === wcId || (!!wcName && String(m.work_center_name || '').trim().toLowerCase() === wcName)
    );
  }, [selectedWorkOrder, selectedStage, warehouseMachines]);

  // Work Orders & Routing stages resolver for Rejection Form
  const selectedPORej = useMemo(() => {
    return warehousePOs.find((po) => po.id === rejectionForm.productionOrderId) || null;
  }, [rejectionForm.productionOrderId, warehousePOs]);

  const selectedPOWorkOrdersRej = useMemo(() => {
    if (!rejectionForm.productionOrderId) return [];
    return workOrders.filter(
      (wo) => wo.production_order_id === rejectionForm.productionOrderId
    );
  }, [rejectionForm.productionOrderId, workOrders]);

  const selectedPOStagesRej = useMemo(() => {
    if (selectedPOWorkOrdersRej.length > 0) return []; // prefer work orders
    if (!selectedPORej || !selectedPORej.routing_id) return [];
    const routing = routings.find((r) => r.id === selectedPORej.routing_id);
    if (!routing) return [];
    try {
      const parsed = typeof routing.stages === 'string' ? JSON.parse(routing.stages) : routing.stages;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('Failed to parse routing stages:', e);
      return [];
    }
  }, [selectedPORej, routings, selectedPOWorkOrdersRej]);

  const selectedWorkOrderRej = useMemo(() => {
    return selectedPOWorkOrdersRej.find((wo) => wo.id === rejectionForm.workOrderId) || null;
  }, [rejectionForm.workOrderId, selectedPOWorkOrdersRej]);

  const selectedStageRej = useMemo(() => {
    const stageNum = Number(rejectionForm.workOrderId);
    return selectedPOStagesRej.find((s) => s.sequence === stageNum) || null;
  }, [rejectionForm.workOrderId, selectedPOStagesRej]);

  const availableMachinesRej = useMemo(() => {
    const wcId = selectedWorkOrderRej?.work_center_id || selectedStageRej?.workCenterId || selectedStageRej?.work_center_id;
    if (!wcId) return warehouseMachines;
    const filtered = warehouseMachines.filter((m) => m.work_center_id === wcId);
    return filtered.length > 0 ? filtered : warehouseMachines;
  }, [selectedWorkOrderRej, selectedStageRej, warehouseMachines]);

  const selectedRejCode = useMemo(() => {
    return warehouseRejectionCodes.find((c) => c.id === rejectionForm.rejectionCodeId) || null;
  }, [rejectionForm.rejectionCodeId, warehouseRejectionCodes]);

  // SearchableSelect options for filters
  const filterPOOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All POs' },
    ...warehousePOs.map((po) => ({
      value: po.id,
      label: po.po_number || po.poNumber,
      sublabel: po.item_name || po.itemName || '',
    })),
  ], [warehousePOs]);

  const filterWCOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Work Centers' },
    ...warehouseWorkCenters.map((wc) => ({ value: wc.id, label: workCenterLookup.labelOf(wc.id, wc.name) })),
  ], [warehouseWorkCenters]);

  const filterOpOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Operators' },
    ...warehouseOperators.map((op) => ({ value: op.id, label: op.name })),
  ], [warehouseOperators]);

  const filterApprovedOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Status' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'PENDING', label: 'Pending Approval' },
  ], []);

  // SearchableSelect options for Entry Form
  const entryPOOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: 'Select Production Order' },
    ...warehousePOs.map((po) => ({
      value: po.id,
      label: `${po.po_number || po.poNumber} — ${po.item_name || po.itemName}`,
    })),
  ], [warehousePOs]);

  const entryWOOptions: SearchableOption[] = useMemo(() => {
    const list: SearchableOption[] = [
      {
        value: '',
        label: form.productionOrderId ? 'Select Work Order' : 'Choose PO first',
      },
    ];
    if (selectedPOWorkOrders.length > 0) {
      selectedPOWorkOrders.forEach((wo: any) => {
        list.push({
          value: wo.id,
          label: `${wo.wo_number} — Stage ${wo.stage_number}: ${wo.stage_name}`,
        });
      });
    } else if (selectedPOStages.length > 0) {
      selectedPOStages.forEach((stage: any) => {
        list.push({
          value: String(stage.sequence),
          label: `Stage ${stage.sequence}: ${stage.operationName}`,
        });
      });
    }
    return list;
  }, [form.productionOrderId, selectedPOWorkOrders, selectedPOStages]);

  const entryRmBatchOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: 'Select Raw Material Batch' },
    ...availableRmBatches.map((b) => ({ value: b, label: b })),
  ], [availableRmBatches]);

  const entryShiftOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: 'Select Shift' },
    ...warehouseShifts.map((s) => ({
      value: s.id,
      label: `${s.name} (${s.start_time || s.startTime || ''} – ${s.end_time || s.endTime || ''})`,
    })),
  ], [warehouseShifts]);

  const entryOperatorOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: 'Select Operator' },
    ...warehouseOperators.map((o) => ({
      value: o.id,
      label: `${o.name} (${o.employee_code || o.employeeCode || ''})`,
    })),
  ], [warehouseOperators]);

  const entryMachineOptions: SearchableOption[] = useMemo(() => {
    const list = entryType === 'DOWNTIME' && availableMachines.length === 0 ? warehouseMachines : availableMachines;
    return [
      {
        value: '',
        label: entryType === 'DOWNTIME' ? 'Select Machine' : form.workOrderId ? 'Select Machine (optional)' : 'Choose WO first',
      },
      ...list.map((m: any) => ({ value: m.id, label: m.name })),
    ];
  }, [form.workOrderId, availableMachines, warehouseMachines, entryType]);

  const entryDowntimeCodeOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: 'Select Downtime Reason' },
    ...downtimeCodes.map((c: any) => ({
      value: c.id,
      label: `${c.code} — ${c.description}`,
      sublabel: c.category ? String(c.category).replace(/_/g, ' ') : undefined,
    })),
  ], [downtimeCodes]);

  const entryRejectionCodeOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: 'Select Rejection Code' },
    ...warehouseRejectionCodes.map((rc) => ({
      value: rc.id,
      label: `${rc.code} — ${rc.description}`,
    })),
  ], [warehouseRejectionCodes]);

  // SearchableSelect options for Rejection / Scrap Form
  const rejPOOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: 'Select Production Order' },
    ...warehousePOs.map((po) => ({
      value: po.id,
      label: `${po.po_number || po.poNumber} — ${po.item_name || po.itemName}`,
    })),
  ], [warehousePOs]);

  const rejWOOptions: SearchableOption[] = useMemo(() => {
    const list: SearchableOption[] = [
      {
        value: '',
        label: rejectionForm.productionOrderId ? 'Select Work Order' : 'Choose PO first',
      },
    ];
    if (selectedPOWorkOrdersRej.length > 0) {
      selectedPOWorkOrdersRej.forEach((wo: any) => {
        list.push({
          value: wo.id,
          label: `${wo.wo_number} — Stage ${wo.stage_number}: ${wo.stage_name}`,
        });
      });
    } else if (selectedPOStagesRej.length > 0) {
      selectedPOStagesRej.forEach((stage: any) => {
        list.push({
          value: String(stage.sequence),
          label: `Stage ${stage.sequence}: ${stage.operationName}`,
        });
      });
    }
    return list;
  }, [rejectionForm.productionOrderId, selectedPOWorkOrdersRej, selectedPOStagesRej]);

  const rejMachineOptions: SearchableOption[] = useMemo(() => [
    {
      value: '',
      label: 'Select Machine (optional)',
    },
    ...availableMachinesRej.map((m) => ({ value: m.id, label: m.name })),
  ], [availableMachinesRej]);

  // Main list filters
  const filtered = useMemo(() => {
    return warehouseEntries.filter((e) => {
      // Production entries only; activity rows stay in the order's activity log
      if (isActivityLog(e)) return false;
      if (dateFilter && e.date !== dateFilter) return false;
      if (poFilter !== 'ALL' && e.productionOrderId !== poFilter) return false;
      if (wcFilter !== 'ALL' && e.workCenterId !== wcFilter) return false;
      if (opFilter !== 'ALL' && e.operatorId !== opFilter) return false;
      if (approvedFilter === 'APPROVED' && e.status !== 'APPROVED') return false;
      if (approvedFilter === 'PENDING' && e.status !== 'PENDING') return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          e.entryNumber.toLowerCase().includes(q) ||
          e.productionOrderNumber.toLowerCase().includes(q) ||
          e.workOrderNumber.toLowerCase().includes(q) ||
          e.stageName.toLowerCase().includes(q) ||
          e.operatorName.toLowerCase().includes(q) ||
          (e.machineName?.toLowerCase().includes(q) ?? false) ||
          e.workCenterName.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [warehouseEntries, search, dateFilter, poFilter, wcFilter, opFilter, approvedFilter]);

  const stats = useMemo(() => {
    // Material issues, downtime, QC, work order start and completion logs are activity rows, not output
    const outputRows = warehouseEntries.filter((e) => !isActivityLog(e));
    const total = outputRows.length;
    const approved = outputRows.filter((e) => e.status === 'APPROVED').length;
    const pending = total - approved;
    const totalProduced = outputRows.reduce((s, e) => s + e.producedQty, 0);
    const totalRejected = outputRows.reduce((s, e) => s + e.rejectedQty, 0);
    return { total, approved, pending, totalProduced, totalRejected };
  }, [warehouseEntries]);

  const rejectionEntries = useMemo(() => {
    return warehouseEntries.filter((e) => e.rejectedQty > 0 && !isActivityLog(e));
  }, [warehouseEntries]);

  const rejectionStats = useMemo(() => {
    const total = rejectionEntries.length;
    const codesUsed = new Set(rejectionEntries.map((r) => r.rejectionCodeId).filter(Boolean)).size;
    const ordersAffected = new Set(rejectionEntries.map((r) => r.productionOrderId).filter(Boolean)).size;
    const totalQty = rejectionEntries.reduce((s, r) => s + r.rejectedQty, 0);
    return { total, codesUsed, ordersAffected, totalQty };
  }, [rejectionEntries]);

  const clearFilters = useCallback(() => {
    setSearch('');
    setDateFilter('');
    setPoFilter('ALL');
    setWcFilter('ALL');
    setOpFilter('ALL');
    setApprovedFilter('ALL');
  }, []);

  const hasFilters =
    search ||
    dateFilter !== '' ||
    poFilter !== 'ALL' ||
    wcFilter !== 'ALL' ||
    opFilter !== 'ALL' ||
    approvedFilter !== 'ALL';

  const openNewEntry = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setReturnUrl(null);
    setShowSlideOver(true);
    getBatchSuggestions('forward', '', selectedWarehouseId || undefined)
      .then((res) => {
        if (res.success && res.data) {
          setAvailableRmBatches(res.data);
        }
      })
      .catch(() => {});
  };

  // Opened from "Complete Work Order": pre-fill a Production Entry for that Work Order
  const entryWoIdFromUrl = searchParams.get('woId');
  useEffect(() => {
    if (!entryWoIdFromUrl || workOrders.length === 0) return;
    const wo = workOrders.find((w) => String(w.id) === String(entryWoIdFromUrl));
    const back = searchParams.get('return_url');
    const next = new URLSearchParams(searchParams);
    next.delete('woId');
    next.delete('return_url');
    setSearchParams(next, { replace: true });
    if (!wo) {
      toast.error('Work Order not found');
      return;
    }

    const sameName = (a: any, b: any) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
    const now = new Date();
    const today = now.toLocaleDateString('en-CA');
    const started = wo.actual_start_date ? new Date(wo.actual_start_date) : null;
    const startedToday = !!started && started.toLocaleDateString('en-CA') === today;
    const alreadyProduced = entries
      .filter((e) => String(e.workOrderId) === String(wo.id))
      .reduce((sum, e) => sum + e.producedQty + e.rejectedQty, 0);
    const remaining = Math.max(0, Number(wo.planned_qty || 0) - alreadyProduced);

    setEditingId(null);
    setEntryType('PRODUCTION_ENTRY');
    setForm({
      ...emptyForm(),
      productionOrderId: wo.production_order_id,
      workOrderId: wo.id,
      date: today,
      shiftId: wo.shift_id || shifts.find((sh) => sameName(sh.name, wo.shift_name))?.id || '',
      operatorId: wo.operator_id || operators.find((o) => sameName(o.name, wo.operator_name))?.id || '',
      machineId: wo.machine_id || machines.find((m) => sameName(m.name, wo.machine_name))?.id || '',
      startTime: startedToday ? started!.toTimeString().slice(0, 5) : '',
      endTime: now.toTimeString().slice(0, 5),
      producedQty: remaining > 0 ? String(remaining) : '',
      outputBatchNumber: generateFGBatchNumber(wo.production_order_id),
    });
    setFormError(null);
    setReturnUrl(back && back.startsWith('/') && !back.startsWith('//') ? back : null);
    setShowSlideOver(true);
  }, [entryWoIdFromUrl, workOrders]);

  // Material limit for the work order picked in the production entry form
  useEffect(() => {
    const wo = workOrders.find((w: any) => String(w.id) === String(form.workOrderId));
    if (!showSlideOver || entryType !== 'PRODUCTION_ENTRY' || !wo?.production_order_id || isReworkWorkOrder(wo)) {
      setMaterialLimit(null);
      return;
    }
    let cancelled = false;
    getFullProductionOrderDetails(wo.production_order_id)
      .then((res: any) => {
        if (cancelled) return;
        if (!res?.success) return setMaterialLimit(null);
        const cap = res.data?.materialCapacity;
        const orderWos: any[] = (res.data?.workOrders || []).filter((w: any) => !isReworkWorkOrder(w));
        const round = (n: number) => Math.max(0, Math.round(n * 1000) / 1000);
        const current = orderWos.find((w) => String(w.id) === String(wo.id)) || wo;
        const used = Number(current.completed_qty || 0) + Number(current.rejected_qty || 0);

        // Each stage is limited by the material issued for that stage (the whole order without a routing)
        const stageCap = Array.isArray(cap?.stages) && cap.stages.length
          ? cap.stages.find((st: any) => Number(st.stageNumber) === Number(wo.stage_number)) || null
          : cap;
        const processed = orderWos
          .filter((w) => Number(w.stage_number) === Number(wo.stage_number))
          .reduce((sum, w) => sum + Number(w.completed_qty || 0) + Number(w.rejected_qty || 0), 0);
        const materialAllowed = stageCap?.hasMaterials ? round(Number(stageCap.materialMax) - processed) : null;

        // A later stage can only work on what the previous stage of its batch passed on
        const batchNo = Number(current.batch_no) || 1;
        const prev = orderWos
          .filter((w) => (Number(w.batch_no) || 1) === batchNo && Number(w.stage_number) < Number(wo.stage_number))
          .sort((a, b) => Number(b.stage_number) - Number(a.stage_number))[0];
        const flowAllowed = prev ? round(Number(prev.completed_qty || 0) - used) : null;

        if (materialAllowed === null && flowAllowed === null) return setMaterialLimit(null);
        setMaterialLimit({
          max: stageCap?.hasMaterials ? Number(stageCap.maxProducible) : null,
          allowed: Math.min(materialAllowed ?? Infinity, flowAllowed ?? Infinity),
          limiting: stageCap?.limiting?.itemName || '',
          prevStage: prev ? `Stage ${prev.stage_number} (${String(prev.stage_name || '').trim()})` : '',
          prevGood: prev ? Number(prev.completed_qty || 0) : null,
        });
      })
      .catch(() => {
        if (!cancelled) setMaterialLimit(null);
      });
    return () => {
      cancelled = true;
    };
  }, [showSlideOver, entryType, form.workOrderId, workOrders]);

  const openEditEntry = (entry: any) => {
    setEditingId(entry.id);
    setEntryType('PRODUCTION_ENTRY');
    setForm({
      productionOrderId: entry.productionOrderId,
      workOrderId: workOrders.some((w) => w.id === entry.workOrderId) ? entry.workOrderId : String(entry.stageNumber),
      date: entry.date,
      shiftId: entry.shiftId,
      operatorId: entry.operatorId,
      machineId: entry.machineId ?? '',
      startTime: entry.startTime.slice(0, 5),
      endTime: entry.endTime.slice(0, 5),
      producedQty: String(entry.producedQty),
      rejectedQty: String(entry.rejectedQty),
      rejectionCodeId: entry.rejectionCodeId ?? '',
      downtimeCodeId: '',
      notes: entry.notes ?? '',
      rawMaterialBatchNumber: entry.rawMaterialBatchNumber ?? '',
      outputBatchNumber: entry.batchNumber ?? '',
    });
    setFormError(null);
    setShowSlideOver(true);
  };

  const handleApprove = async (id: string) => {
    try {
      const res = await approveProductionEntry(id);
      if (res.success) {
        // Says when the stage's QC inspection was raised or resized for the approved quantity
        toast.success(res.message || 'Production entry approved successfully');
        loadData();
      } else {
        toast.error(res.message || 'Failed to approve entry');
      }
    } catch (err: any) {
      toast.error(err.message || 'Server error occurred');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      const res = await deleteProductionEntry(id);
      if (res.success) {
        toast.success('Production entry deleted successfully');
        loadData();
      } else {
        toast.error(res.message || 'Failed to delete entry');
      }
    } catch (err: any) {
      toast.error(err.message || 'Server error occurred');
    }
  };

  // Material Issue: load the selected order's materials (required / reserved / issued) from its reservations
  useEffect(() => {
    if (!showSlideOver || entryType !== 'MATERIAL_ISSUE') return;
    if (!form.productionOrderId) {
      setSfMaterialItems([]);
      return;
    }
    let cancelled = false;
    setSfMaterialsLoading(true);
    getMaterialReservations(form.productionOrderId)
      .then((res) => {
        if (cancelled) return;
        setSfMaterialItems(
          (res?.data || []).map((r: any) => {
            const req = Number(r.requiredQty || 0);
            const reserved = Number(r.reservedQty || 0);
            const issued = Number(r.issuedQty || 0);
            const stockAvailable = Number(r.stockAvailable || 0);
            const maxIssuableQty = Math.min(Math.max(0, req - issued), reserved > 0 ? reserved : stockAvailable);
            return {
              itemId: r.itemId,
              itemName: r.itemName,
              itemCode: r.itemCode,
              requiredQty: req,
              reservedQty: reserved,
              alreadyIssued: issued,
              stockAvailable,
              maxIssuableQty,
              issueNowQty: maxIssuableQty,
              unit: r.unit,
            };
          }),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setSfMaterialItems([]);
        toast.error('Failed to load materials for this production order');
      })
      .finally(() => {
        if (!cancelled) setSfMaterialsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showSlideOver, entryType, form.productionOrderId]);

  const issueOperatorOptions = useMemo(() => {
    const po = productionOrders.find((p) => p.id === form.productionOrderId);
    const whId = po?.warehouse_id ?? po?.warehouseId ?? selectedWarehouseId;
    return operators.filter((o) => {
      const wh = o.warehouse_id ?? o.warehouseId;
      return (o.is_active ?? o.isActive ?? true) && (!wh || !whId || String(wh) === String(whId));
    });
  }, [operators, productionOrders, form.productionOrderId, selectedWarehouseId]);

  const generateFGBatchNumber = (poId: string) => {
    const po = warehousePOs.find((p) => p.id === poId);
    const poCode = (po?.po_number || po?.poNumber || 'PRD').replace(/[^a-zA-Z0-9]/g, '').substring(0, 6).toUpperCase();
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const randSuffix = String(Math.floor(100 + Math.random() * 900));
    return `BT-FG-${poCode}-${dateStr}-${randSuffix}`;
  };

  const updateForm = (patch: Partial<FormState>) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      if (patch.productionOrderId !== undefined) {
        next.workOrderId = '';
        next.machineId = '';
        if (patch.productionOrderId) {
          next.outputBatchNumber = generateFGBatchNumber(patch.productionOrderId);
        }
      }
      if (patch.workOrderId !== undefined) {
        next.machineId = '';
      }
      return next;
    });
    setFormError(null);
  };

  const handleSave = async () => {
    if (!form.productionOrderId) return setFormError('Production Order is required');

    if (entryType === 'MATERIAL_ISSUE') {
      const po = warehousePOs.find((p) => p.id === form.productionOrderId);
      if (!po) return setFormError('Invalid Production Order');

      const issuedBy = operators.find((o) => String(o.id) === String(form.operatorId));
      if (!issuedBy) return setFormError('Issued By (Operator) is required');
      if (!form.date) return setFormError('Date is required');

      const itemsToIssue = sfMaterialItems
        .filter((item) => Number(item.issueNowQty) > 0)
        .map((item) => ({
          item_id: item.itemId,
          quantity: Number(item.issueNowQty),
          qty_issued: Number(item.issueNowQty),
          warehouse_id: po.warehouse_id,
        }));
      if (itemsToIssue.length === 0) return setFormError('Enter a quantity to issue for at least one item');

      try {
        const res = await createProductionEntry({
          entry_type: 'MATERIAL_ISSUE',
          prd_id: po.id,
          productionOrderId: po.id,
          productionOrderNumber: po.po_number || po.poNumber,
          operatorId: issuedBy.id,
          operator_name: issuedBy.name,
          issue_date_time: `${form.date}T${new Date().toTimeString().slice(0, 5)}`,
          warehouseId: po.warehouse_id || null,
          items: itemsToIssue,
        });
        if (!res.success) return setFormError(res.message || 'Error issuing materials');

        toast.success('Materials issued successfully');
        setShowSlideOver(false);
        loadData();
      } catch (err: any) {
        setFormError(err.response?.data?.message || err.message || 'Error issuing materials');
      }
      return;
    }

    if (entryType === 'DOWNTIME') {
      // Logged in the Downtime module, so it shows on the Downtime page and counts in OEE
      if (!form.machineId) return setFormError('Machine is required');
      if (!form.downtimeCodeId) return setFormError('Downtime Reason is required');
      if (!form.date) return setFormError('Date is required');
      if (!form.shiftId) return setFormError('Shift is required');
      if (!form.operatorId) return setFormError('Operator is required');
      if (!form.startTime) return setFormError('Start Time is required');
      if (form.endTime && form.endTime <= form.startTime) return setFormError('End Time must be after Start Time');

      try {
        const po = warehousePOs.find((p) => p.id === form.productionOrderId);
        const res = await createDowntimeEntry({
          machineId: form.machineId,
          downtimeCodeId: form.downtimeCodeId,
          date: form.date,
          startTime: form.startTime,
          productionOrderId: form.productionOrderId || null,
          shiftId: form.shiftId,
          operatorId: form.operatorId,
          description: form.notes || null,
          warehouseId: po?.warehouse_id || selectedWarehouseId || null,
        });
        if (!res.success) return setFormError(res.message || 'Failed to log downtime');

        // An end time means the stoppage is already over
        if (form.endTime && res.data?.id) {
          const operator = operators.find((o) => o.id === form.operatorId);
          await resolveDowntimeEntry(res.data.id, { endTime: form.endTime, resolvedBy: operator?.name });
        }

        toast.success(form.endTime ? 'Downtime logged and resolved' : 'Downtime logged — resolve it on the Downtime page when the machine is back');
        setShowSlideOver(false);
        loadData();
      } catch (err: any) {
        setFormError(err.response?.data?.message || err.message || 'Error logging downtime');
      }
      return;
    }

    if (entryType === 'REJECTION') {
      const po = warehousePOs.find((p) => p.id === form.productionOrderId);
      const wo = selectedWorkOrder;
      const shift = warehouseShifts.find((sh) => sh.id === form.shiftId);
      const operator = warehouseOperators.find((o) => o.id === form.operatorId);
      const machine = warehouseMachines.find((m) => m.id === form.machineId) || null;
      if (!po) return setFormError('Invalid Production Order');
      if (!wo) return setFormError('Work Order / Stage is required');
      if (!form.date) return setFormError('Date is required');
      if (!shift) return setFormError('Shift is required');
      if (!operator) return setFormError('Operator is required');
      if (!form.rejectedQty || Number(form.rejectedQty) <= 0) return setFormError('Rejected Qty must be greater than 0');
      if (!form.rejectionCodeId) return setFormError('Rejection Code is required');

      try {
        const now = new Date().toTimeString().slice(0, 5);
        const res = await createProductionEntry({
          entry_type: 'REJECTION',
          productionOrderId: po.id,
          productionOrderNumber: po.po_number || po.poNumber,
          workOrderId: wo.id,
          workOrderNumber: wo.wo_number || wo.woNumber,
          stageName: wo.stage_name || wo.stageName,
          stageNumber: wo.stage_number ?? wo.stageNumber,
          workCenterId: wo.work_center_id || wo.workCenterId,
          workCenterName: wo.work_center_name || wo.workCenterName,
          machineId: machine?.id ?? null,
          machineName: machine?.name ?? null,
          operatorId: operator.id,
          operatorName: operator.name,
          shiftId: shift.id,
          shiftName: shift.name,
          date: form.date,
          producedQty: 0,
          rejectedQty: Number(form.rejectedQty),
          unit: po.unit || undefined,
          startTime: now,
          endTime: now,
          actualTimeMinutes: 0,
          rejectionCodeId: form.rejectionCodeId,
          notes: form.notes || null,
          warehouseId: po.warehouse_id || selectedWarehouseId || null,
        });
        if (!res.success) return setFormError(res.message || 'Failed to log rejection');

        toast.success('Rejection logged successfully');
        setShowSlideOver(false);
        if (returnUrl) {
          navigate(returnUrl);
          return;
        }
        loadData();
      } catch (err: any) {
        setFormError(err.response?.data?.message || err.message || 'Error logging rejection');
      }
      return;
    }

    if (!form.workOrderId) return setFormError('Work Order / Stage is required');
    if (!form.date) return setFormError('Date is required');
    if (!form.shiftId) return setFormError('Shift is required');
    if (!form.operatorId) return setFormError('Operator is required');
    if (!form.startTime) return setFormError('Start Time is required');
    if (!form.endTime) return setFormError('End Time is required');
    if (form.startTime >= form.endTime) return setFormError('End Time must be after Start Time');
    if (form.producedQty === '' || Number(form.producedQty) < 0) return setFormError('Produced Qty is required');
    if (form.rejectedQty !== '' && Number(form.rejectedQty) < 0) return setFormError('Rejected Qty cannot be negative');
    // Produced 0 is fine when the whole batch was rejected; an entry with neither is not
    if (Number(form.producedQty) + Number(form.rejectedQty || 0) <= 0) return setFormError('Enter a Produced or Rejected Qty greater than 0');
    {
      // Output is counted in the finished good's unit: no 2.5 Pcs
      const fgUnit = productionOrders.find((p) => p.id === form.productionOrderId)?.unit;
      const decimalError = units.decimalError(Number(form.producedQty), fgUnit) ?? units.decimalError(Number(form.rejectedQty || 0), fgUnit);
      if (decimalError) return setFormError(decimalError);
    }
    if (!editingId && materialLimit && Number(form.producedQty) + Number(form.rejectedQty || 0) > materialLimit.allowed) {
      return setFormError(
        materialLimit.prevGood !== null
          ? `Only ${materialLimit.prevGood} unit(s) came out of ${materialLimit.prevStage}, so ${materialLimit.allowed} more can be recorded here. Remaining units run as a separate batch from Stage 1.`
          : `Insufficient materials issued. Only ${materialLimit.allowed} more unit(s) can be recorded at this stage`
            + (materialLimit.limiting ? ` — issue more ${materialLimit.limiting} to produce additional units.` : '.'),
      );
    }
    if (Number(form.rejectedQty || 0) > 0 && !form.rejectionCodeId) {
      return setFormError('Rejection Code is required when rejected qty > 0');
    }

    const producedQty = Number(form.producedQty);
    const rejectedQty = Number(form.rejectedQty || 0);
    const actualTimeMinutes = timeToMinutes(form.endTime) - timeToMinutes(form.startTime);

    const po = warehousePOs.find((p) => p.id === form.productionOrderId);
    // Prefer real work order from DB; fall back to routing stage
    const wo = selectedWorkOrder;
    const stage = !wo ? selectedPOStages.find((s) => s.sequence === Number(form.workOrderId)) : null;
    const shift = warehouseShifts.find((s) => s.id === form.shiftId);
    const operator = warehouseOperators.find((o) => o.id === form.operatorId);
    const machine = warehouseMachines.find((m) => m.id === form.machineId) || null;

    if (!po || (!wo && !stage) || !shift || !operator) {
      return setFormError('Invalid selection — please check fields');
    }

    const payload = {
      productionOrderId: po.id,
      productionOrderNumber: po.po_number || po.poNumber,
      workOrderId: wo ? wo.id : `${po.id}-stage-${stage!.sequence}`,
      workOrderNumber: wo ? (wo.wo_number || wo.woNumber) : `${po.po_number || po.poNumber}-ST-${stage!.sequence}`,
      stageName: wo ? (wo.stage_name || wo.stageName) : stage!.operationName,
      stageNumber: wo ? (wo.stage_number ?? wo.stageNumber) : stage!.sequence,
      workCenterId: wo ? (wo.work_center_id || wo.workCenterId) : stage!.workCenterId,
      workCenterName: wo ? (wo.work_center_name || wo.workCenterName) : stage!.workCenterName,
      machineId: machine?.id ?? null,
      machineName: machine?.name ?? null,
      operatorId: operator.id,
      operatorName: operator.name,
      shiftId: shift.id,
      shiftName: shift.name,
      date: form.date,
      producedQty,
      rejectedQty,
      unit: po.unit || 'Pcs',
      startTime: form.startTime,
      endTime: form.endTime,
      actualTimeMinutes,
      rejectionCodeId: form.rejectionCodeId || null,
      notes: form.notes || null,
      warehouseId: selectedWarehouseId || po.warehouse_id || null,
      batch_number: form.outputBatchNumber || undefined,
      output_batch_number: form.outputBatchNumber || undefined,
      raw_material_batch_number: form.rawMaterialBatchNumber || undefined,
      input_batch_number: form.rawMaterialBatchNumber || undefined,
    };

    try {
      if (editingId) {
        const res = await updateProductionEntry(editingId, payload);
        if (res.success) {
          toast.success('Production entry updated successfully');
          setShowSlideOver(false);
          loadData();
        } else {
          setFormError(res.message || 'Failed to update entry');
        }
      } else {
        const res = await createProductionEntry(payload);
        if (res.success) {
          toast.success('Production entry saved successfully');
          // QC-enabled stage: the backend raises (or refreshes) the stage's in-process inspection
          const qcInsp = (res as any).qc_inspection;
          if (qcInsp) {
            toast.info(
              qcInsp.created
                ? `QC inspection ${qcInsp.inspection_number} raised for this stage${qcInsp.checklist_name ? ` (${qcInsp.checklist_name})` : ''} — pending inspection`
                : `QC inspection ${qcInsp.inspection_number} updated with the new quantity`
            );
          }
          setShowSlideOver(false);
          if (returnUrl) {
            navigate(returnUrl);
            return;
          }
          loadData();
        } else {
          setFormError(res.message || 'Failed to save entry');
        }
      }
    } catch (error: any) {
      setFormError(error?.response?.data?.message || error?.message || 'Server error occurred');
    }
  };

  const openRejectionForm = () => {
    setRejectionForm({
      productionOrderId: '',
      workOrderId: '',
      date: toInputDate(new Date()),
      shiftId: '',
      operatorId: '',
      machineId: '',
      rejectionCodeId: '',
      rejectedQty: '',
      notes: '',
    });
    setRejectionError(null);
    setShowRejectionSlideOver(true);
  };

  const handleSaveRejection = async () => {
    if (!rejectionForm.productionOrderId) return setRejectionError('Production Order is required');
    if (!rejectionForm.workOrderId) return setRejectionError('Work Order / Stage is required');
    if (!rejectionForm.date) return setRejectionError('Date is required');
    if (!rejectionForm.shiftId) return setRejectionError('Shift is required');
    if (!rejectionForm.operatorId) return setRejectionError('Operator is required');
    if (!rejectionForm.rejectionCodeId) return setRejectionError('Rejection Code is required');
    if (!rejectionForm.rejectedQty || Number(rejectionForm.rejectedQty) <= 0) {
      return setRejectionError('Rejected Qty is required and must be > 0');
    }

    const rejectedQty = Number(rejectionForm.rejectedQty);
    const po = warehousePOs.find((p) => p.id === rejectionForm.productionOrderId);
    // Prefer real work order from DB; fall back to routing stage
    const wo = selectedWorkOrderRej;
    const stage = !wo ? selectedPOStagesRej.find((s) => s.sequence === Number(rejectionForm.workOrderId)) : null;
    const shift = warehouseShifts.find((s) => s.id === rejectionForm.shiftId);
    const operator = warehouseOperators.find((o) => o.id === rejectionForm.operatorId);
    const machine = warehouseMachines.find((m) => m.id === rejectionForm.machineId) || null;

    if (!po || (!wo && !stage) || !shift || !operator) {
      return setRejectionError('Invalid selection');
    }

    const payload = {
      productionOrderId: po.id,
      productionOrderNumber: po.po_number || po.poNumber,
      workOrderId: wo ? wo.id : `${po.id}-stage-${stage!.sequence}`,
      workOrderNumber: wo ? (wo.wo_number || wo.woNumber) : `${po.po_number || po.poNumber}-ST-${stage!.sequence}`,
      stageName: wo ? (wo.stage_name || wo.stageName) : stage!.operationName,
      stageNumber: wo ? (wo.stage_number ?? wo.stageNumber) : stage!.sequence,
      workCenterId: wo ? (wo.work_center_id || wo.workCenterId) : stage!.workCenterId,
      workCenterName: wo ? (wo.work_center_name || wo.workCenterName) : stage!.workCenterName,
      machineId: machine?.id ?? null,
      machineName: machine?.name ?? null,
      operatorId: operator.id,
      operatorName: operator.name,
      shiftId: shift.id,
      shiftName: shift.name,
      date: rejectionForm.date,
      producedQty: 0,
      rejectedQty,
      unit: po.unit || 'Pcs',
      entry_type: 'REJECTION',
      startTime: new Date().toTimeString().slice(0, 5),
      endTime: new Date().toTimeString().slice(0, 5),
      actualTimeMinutes: 0,
      rejectionCodeId: rejectionForm.rejectionCodeId,
      notes: rejectionForm.notes || null,
      warehouseId: selectedWarehouseId || po.warehouse_id || null,
    };

    try {
      const res = await createProductionEntry(payload);
      if (res.success) {
        toast.success('Rejection entry logged successfully');
        setShowRejectionSlideOver(false);
        loadData();
      } else {
        setRejectionError(res.message || 'Failed to log rejection');
      }
    } catch (error: any) {
      setRejectionError(error?.response?.data?.message || error?.message || 'Server error occurred');
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6">
        <div className="max-w-[1400px] mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
            <div>
              <h1 className="text-xl font-bold text-[#1e293b]">
                Production Entry
              </h1>
              <p className="text-xs text-[#64748b] mt-0.5">
                {activeTab === 'entries'
                  ? `${stats.total} entries recorded`
                  : `${rejectionStats.total} rejections logged`}
              </p>
            </div>
            {activeTab === 'entries' ? (
              <button
                onClick={openNewEntry}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2563eb] text-white text-sm font-medium hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex"
              >
                <i className="ri-add-line" />
                New Entry
              </button>
            ) : (
              <button
                onClick={openRejectionForm}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 transition-colors cursor-pointer whitespace-nowrap flex"
              >
                <i className="ri-add-line" />
                Log Rejection
              </button>
            )}
          </div>

          {/* Stats */}
          {activeTab === 'entries' ? (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
              <div className="bg-white border border-slate-200 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-indigo-100">
                  <i className="ri-file-list-3-line text-indigo-600 text-base" />
                </div>
                <div className="text-xs text-indigo-700 font-medium uppercase tracking-wide">
                  Total Entries
                </div>
                <div className="text-2xl font-bold text-indigo-700 mt-1">
                  {stats.total}
                </div>
              </div>
              <div className="bg-white border border-emerald-200 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100">
                  <i className="ri-check-double-line text-emerald-600 text-base" />
                </div>
                <div className="text-xs text-emerald-700 font-medium uppercase tracking-wide">
                  Approved
                </div>
                <div className="text-2xl font-bold text-emerald-700 mt-1">
                  {stats.approved}
                </div>
              </div>
              <div className="bg-white border border-amber-200 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-amber-100">
                  <i className="ri-time-line text-amber-600 text-base" />
                </div>
                <div className="text-xs text-amber-700 font-medium uppercase tracking-wide">
                  Pending
                </div>
                <div className="text-2xl font-bold text-amber-700 mt-1">
                  {stats.pending}
                </div>
              </div>
              <div className="bg-white border border-sky-200 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-sky-100">
                  <i className="ri-box-3-line text-sky-600 text-base" />
                </div>
                <div className="text-xs text-sky-700 font-medium uppercase tracking-wide">
                  Total Produced
                </div>
                <div className="flex items-baseline gap-1.5 mt-1">
                  <span className="text-2xl font-bold text-sky-700">
                    {stats.totalProduced}
                  </span>
                  <span className="text-xs font-medium text-sky-600">
                    Units
                  </span>
                </div>
              </div>
              <div className="bg-white border border-rose-200 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-rose-100">
                  <i className="ri-close-circle-line text-rose-600 text-base" />
                </div>
                <div className="text-xs text-rose-700 font-medium uppercase tracking-wide">
                  Total Rejected
                </div>
                <div className="flex items-baseline gap-1.5 mt-1">
                  <span className="text-2xl font-bold text-rose-700">
                    {stats.totalRejected}
                  </span>
                  <span className="text-xs font-medium text-rose-500">
                    Units ({rejectionStats.total} {rejectionStats.total === 1 ? 'entry' : 'entries'})
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <div className="bg-white border border-slate-200 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-rose-100">
                  <i className="ri-close-circle-line text-rose-600 text-base" />
                </div>
                <div className="text-xs text-rose-700 font-medium uppercase tracking-wide">
                  Total Rejections
                </div>
                <div className="text-2xl font-bold text-rose-700 mt-1">
                  {rejectionStats.total}
                </div>
              </div>
              <div className="bg-white border border-amber-200 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-amber-100">
                  <i className="ri-tools-line text-amber-600 text-base" />
                </div>
                <div className="text-xs text-amber-700 font-medium uppercase tracking-wide">
                  Rejection Codes Used
                </div>
                <div className="text-2xl font-bold text-amber-700 mt-1">
                  {rejectionStats.codesUsed}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100">
                  <i className="ri-delete-bin-line text-slate-600 text-base" />
                </div>
                <div className="text-xs text-slate-700 font-medium uppercase tracking-wide">
                  Orders Affected
                </div>
                <div className="text-2xl font-bold text-slate-700 mt-1">
                  {rejectionStats.ordersAffected}
                </div>
              </div>
              <div className="bg-white border border-rose-200 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute right-3 top-3 w-8 h-8 flex items-center justify-center rounded-full bg-rose-100">
                  <i className="ri-stack-line text-rose-600 text-base" />
                </div>
                <div className="text-xs text-rose-700 font-medium uppercase tracking-wide">
                  Total Qty
                </div>
                <div className="text-2xl font-bold text-rose-700 mt-1">
                  {rejectionStats.totalQty}
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white border border-slate-200 rounded-t-xl border-b-0">
            <div className="flex p-1 gap-1">
              <button
                onClick={() => setActiveTab('entries')}
                className={`px-4 py-2 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors ${activeTab === 'entries'
                  ? 'bg-slate-100 text-[#1e293b]'
                  : 'text-[#64748b] hover:bg-slate-50'
                  }`}
              >
                <i className="ri-file-list-3-line mr-1.5" />
                Production Entries
              </button>
              <button
                onClick={() => setActiveTab('rejections')}
                className={`px-4 py-2 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors ${activeTab === 'rejections'
                  ? 'bg-slate-100 text-[#1e293b]'
                  : 'text-[#64748b] hover:bg-slate-50'
                  }`}
              >
                <i className="ri-close-circle-line mr-1.5" />
                Rejections
                <span
                  className="ml-1.5 inline-flex items-center justify-center px-1.5 h-5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold"
                  title={`${rejectionStats.total} entries (${stats.totalRejected} units)`}
                >
                  {rejectionStats.total}
                </span>
              </button>
            </div>
          </div>

          {activeTab === 'entries' && (
            <>
              {/* Filters */}
              <div className="bg-white border border-slate-200 rounded-b-xl p-3 mb-4 -mt-px">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                          <i className="ri-search-line text-sm" />
                        </div>
                        <input
                          type="text"
                          placeholder="Search by entry number, PO, WO, operator..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="date"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 cursor-pointer"
                      />
                      <div className="w-44">
                        <SearchableSelect
                          value={poFilter}
                          onChange={setPoFilter}
                          options={filterPOOptions}
                          placeholder="All POs"
                          searchPlaceholder="Search PO..."
                        />
                      </div>
                      <div className="w-44">
                        <SearchableSelect
                          value={wcFilter}
                          onChange={setWcFilter}
                          options={filterWCOptions}
                          placeholder="All Work Centers"
                          searchPlaceholder="Search center..."
                        />
                      </div>
                      <div className="w-44">
                        <SearchableSelect
                          value={opFilter}
                          onChange={setOpFilter}
                          options={filterOpOptions}
                          placeholder="All Operators"
                          searchPlaceholder="Search operator..."
                        />
                      </div>
                      <div className="w-36">
                        <SearchableSelect
                          value={approvedFilter}
                          onChange={(v) => setApprovedFilter(v as typeof approvedFilter)}
                          options={filterApprovedOptions}
                          placeholder="All Status"
                          searchPlaceholder="Search status..."
                        />
                      </div>
                    </div>
                  </div>
                  {hasFilters && (
                    <div className="flex justify-end">
                      <button
                        onClick={clearFilters}
                        className="text-xs text-[#64748b] hover:text-[#1e293b] cursor-pointer underline"
                      >
                        Clear all filters
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Entry #
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Date
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          PO / WO
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Stage
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Work Center
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Machine
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Operator
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Shift
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Produced
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Rejected
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Time
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Approval
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={13} className="px-4 py-10 text-center text-sm text-[#94a3b8]">
                            Loading production entries...
                          </td>
                        </tr>
                      ) : filtered.length === 0 ? (
                        <tr>
                          <td colSpan={13} className="px-4 py-10 text-center text-sm text-[#94a3b8]">
                            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-slate-50 border border-slate-200 mb-3">
                              <i className="ri-inbox-line text-xl text-slate-300" />
                            </div>
                            No production entries match your filters
                          </td>
                        </tr>
                      ) : (
                        filtered.map((e) => (
                          <tr
                            key={e.id}
                            className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm font-semibold text-[#4f46e5]">
                                {e.entryNumber}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-[#475569] text-xs">
                              {e.date}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-xs font-medium text-[#1e293b]">
                                {e.productionOrderNumber}
                              </div>
                              <div className="text-[11px] text-[#94a3b8]">
                                {e.workOrderNumber}
                              </div>
                              {e.batchNumber && (
                                <div className="mt-1">
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    <i className="ri-barcode-line text-[11px]" />
                                    {e.batchNumber}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-[#1e293b]">
                              {e.stageName}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-[#475569]">
                              {workCenterLookup.labelOf((e as any).workCenterId ?? (e as any).work_center_id, e.workCenterName)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-[#475569]">
                              {e.machineName ?? '—'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-[#475569]">
                              {e.operatorName}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border bg-slate-50 text-slate-600 border-slate-200">
                                {e.shiftName}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm font-semibold text-emerald-700">
                                {e.producedQty}
                              </span>
                              <span className="text-[11px] text-[#94a3b8] ml-1">
                                {e.unit}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {e.rejectedQty > 0 ? (
                                <span className="text-sm font-semibold text-rose-700">
                                  {e.rejectedQty}
                                </span>
                              ) : (
                                <span className="text-sm text-[#94a3b8]">
                                  0
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-[#475569]">
                              {e.startTime} – {e.endTime}
                              <div className="text-[11px] text-[#94a3b8]">
                                {e.actualTimeMinutes} min
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {e.isApproved ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">
                                  <i className="ri-check-line" /> Approved
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border bg-amber-50 text-amber-700 border-amber-200">
                                  <i className="ri-time-line" /> Pending
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {!e.isApproved ? (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleApprove(e.id)}
                                    className="px-2 py-1 text-[11px] font-medium rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 cursor-pointer"
                                    title="Approve"
                                  >
                                    <i className="ri-check-line" />
                                  </button>
                                  <button
                                    onClick={() => openEditEntry(e)}
                                    className="px-2 py-1 text-[11px] font-medium rounded-md bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100 cursor-pointer"
                                    title="Edit"
                                  >
                                    <i className="ri-edit-line" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(e.id)}
                                    className="px-2 py-1 text-[11px] font-medium rounded-md bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 cursor-pointer"
                                    title="Delete"
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-[#94a3b8]">
                                  —
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50/50">
                  <span className="text-xs text-[#64748b]">
                    Showing {filtered.length} of {warehouseEntries.length} entries
                  </span>
                </div>
              </div>
            </>
          )}

          {activeTab === 'rejections' && (
            <>
              {/* Rejection Filters placeholder */}
              <div className="bg-white border border-slate-200 rounded-b-xl p-3 mb-4 -mt-px">
                <div className="text-xs text-[#64748b]">
                  {rejectionEntries.length} rejection entries on record
                </div>
              </div>

              {/* Rejection Table */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Entry #
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          PO
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Stage
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Code
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Qty
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Rejection Code
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Date
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Operator
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-[#475569] text-xs uppercase tracking-wider whitespace-nowrap">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-10 text-center text-sm text-[#94a3b8]">
                            Loading rejection entries...
                          </td>
                        </tr>
                      ) : rejectionEntries.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-10 text-center text-sm text-[#94a3b8]">
                            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-slate-50 border border-slate-200 mb-3">
                              <i className="ri-inbox-line text-xl text-slate-300" />
                            </div>
                            No rejection entries logged
                          </td>
                        </tr>
                      ) : (
                        rejectionEntries.map((r) => (
                          <tr
                            key={r.id}
                            className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm font-semibold text-[#4f46e5]">
                                {r.entryNumber}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-xs font-medium text-[#1e293b]">
                                {r.productionOrderNumber}
                              </div>
                              <div className="text-[11px] text-[#94a3b8]">
                                {r.workOrderNumber}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-[#475569]">
                              {r.stageName}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border bg-rose-50 text-rose-700 border-rose-200">
                                {
                                  warehouseRejectionCodes.find((c) => c.id === r.rejectionCodeId)?.code ??
                                  'REJ'
                                }
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm font-semibold text-rose-700">
                                {r.rejectedQty}
                              </span>
                              <span className="text-[11px] text-[#94a3b8] ml-1">
                                {r.unit}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border bg-rose-50 text-rose-700 border-rose-200">
                                {(() => {
                                  const code = rejectionCodes.find((c: any) => c.id === r.rejectionCodeId);
                                  return code ? `${code.code} — ${code.description}` : '—';
                                })()}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-[#475569]">
                              {r.date}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-[#475569]">
                              {r.operatorName}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleDelete(r.id)}
                                  className="px-2 py-1 text-[11px] font-medium rounded-md bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 cursor-pointer"
                                  title="Delete"
                                >
                                  <i className="ri-delete-bin-line" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50/50">
                  <span className="text-xs text-[#64748b]">
                    Showing {rejectionEntries.length} of {rejectionEntries.length} entries
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Production Entry Slide-over */}
      {showSlideOver && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowSlideOver(false)}
          />
          <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-[#1e293b]">
                {editingId ? 'Edit Production Entry' : 'Log Production Entry'}
              </h2>
              <button
                onClick={() => setShowSlideOver(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-5 space-y-5 flex-1">
              {formError && (
                <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
                  {formError}
                </div>
              )}

              {/* Entry Type Selector */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">
                  Entry Type
                </label>
                <div className="grid grid-cols-3 gap-1 p-1 bg-slate-100 rounded-xl border border-slate-200 text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setEntryType('MATERIAL_ISSUE')}
                    className={`py-1.5 rounded-lg text-center transition-all cursor-pointer ${
                      entryType === 'MATERIAL_ISSUE'
                        ? 'bg-indigo-600 text-white shadow-sm font-bold'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                    }`}
                  >
                    Material Issue
                  </button>
                  <button
                    type="button"
                    onClick={() => setEntryType('PRODUCTION_ENTRY')}
                    className={`py-1.5 rounded-lg text-center transition-all cursor-pointer ${
                      entryType === 'PRODUCTION_ENTRY'
                        ? 'bg-indigo-600 text-white shadow-sm font-bold'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                    }`}
                  >
                    Production Entry
                  </button>
                  {/* <button
                    type="button"
                    onClick={() => setEntryType('DOWNTIME')}
                    className={`py-1.5 rounded-lg text-center transition-all cursor-pointer ${
                      entryType === 'DOWNTIME'
                        ? 'bg-indigo-600 text-white shadow-sm font-bold'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                    }`}
                  >
                    Downtime
                  </button> */}
                  <button
                    type="button"
                    onClick={() => setEntryType('REJECTION')}
                    className={`py-1.5 rounded-lg text-center transition-all cursor-pointer ${
                      entryType === 'REJECTION'
                        ? 'bg-indigo-600 text-white shadow-sm font-bold'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                    }`}
                  >
                    Rejection
                  </button>
                </div>
              </div>

              {/* Production Order */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Production Order <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={form.productionOrderId}
                  onChange={(val) => updateForm({ productionOrderId: val })}
                  options={entryPOOptions}
                  placeholder="Select Production Order"
                  searchPlaceholder="Search order..."
                />
              </div>

              {/* Material Issue Specific Form */}
              {entryType === 'MATERIAL_ISSUE' && (
                <div className="space-y-4">
                  <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                          <tr>
                            <th className="px-3 py-2">Item Name</th>
                            <th className="px-3 py-2 text-right">Total Required Qty</th>
                            <th className="px-3 py-2 text-right text-emerald-800 bg-emerald-50/60">Reserved Qty</th>
                            <th className="px-3 py-2 text-right text-indigo-800">Already Issued</th>
                            <th className="px-3 py-2 text-right w-24 text-indigo-900 bg-indigo-50/70">Issue Now Qty</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {sfMaterialItems.length === 0 && (
                            <tr>
                              <td colSpan={5} className="px-3 py-6 text-center text-slate-400">
                                {!form.productionOrderId
                                  ? 'Select a production order to load its materials'
                                  : sfMaterialsLoading
                                    ? 'Loading materials…'
                                    : 'No materials found for this production order'}
                              </td>
                            </tr>
                          )}
                          {sfMaterialItems.map((item, idx) => {
                            const remaining = Math.max(0, item.requiredQty - item.alreadyIssued);
                            return (
                              <tr key={item.itemId || idx} className="hover:bg-slate-50/70">
                                <td className="px-3 py-2 font-semibold text-slate-800">
                                  {item.itemName}{' '}
                                  {item.itemCode && <span className="text-slate-400 font-normal">({item.itemCode})</span>}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-slate-600">
                                  {item.requiredQty.toFixed(2)} {item.unit}
                                </td>
                                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700 bg-emerald-50/30">
                                  {item.reservedQty.toFixed(2)} {item.unit}
                                </td>
                                <td className="px-3 py-2 text-right font-mono text-indigo-600">
                                  {item.alreadyIssued.toFixed(2)} {item.unit}
                                </td>
                                <td className="px-3 py-2 text-right bg-indigo-50/20">
                                  {remaining <= 0 ? (
                                    <span className="text-[10px] text-emerald-600 font-medium">Fully issued</span>
                                  ) : item.maxIssuableQty <= 0 ? (
                                    <span className="text-[10px] text-rose-500 font-medium">Out of Stock</span>
                                  ) : (
                                    <input
                                      type="number"
                                      min={0}
                                      max={item.maxIssuableQty}
                                      step="any"
                                      value={item.issueNowQty}
                                      onChange={(e) => {
                                        const val = Math.min(item.maxIssuableQty, Math.max(0, Number(e.target.value)));
                                        setSfMaterialItems((prev) =>
                                          prev.map((it, i) => (i === idx ? { ...it, issueNowQty: val } : it)),
                                        );
                                      }}
                                      className="w-20 h-7 px-1 text-right border border-indigo-300 rounded text-xs font-mono font-bold focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-indigo-50/30"
                                    />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Operator */}
                  <div>
                    <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                      Issued By (Operator) <span className="text-rose-500">*</span>
                    </label>
                    <select
                      value={form.operatorId}
                      onChange={(e) => updateForm({ operatorId: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 cursor-pointer bg-white"
                    >
                      <option value="">Select Operator</option>
                      {issueOperatorOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}{o.employee_code || o.employeeCode ? ` (${o.employee_code || o.employeeCode})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {entryType !== 'MATERIAL_ISSUE' && (
                <>
              {/* Work Order / Stage */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Work Order / Stage {entryType !== 'DOWNTIME' && <span className="text-rose-500">*</span>}
                </label>
                <SearchableSelect
                  value={form.workOrderId}
                  onChange={(val) => updateForm({ workOrderId: val })}
                  options={entryWOOptions}
                  disabled={!form.productionOrderId}
                  placeholder={form.productionOrderId ? 'Select Work Order' : 'Choose PO first'}
                  searchPlaceholder="Search stage/WO..."
                />
                {selectedPOWorkOrders.length === 0 && selectedPOStages.length === 0 && form.productionOrderId && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    No work orders exist for this PO yet. Create work orders first.
                  </p>
                )}
              </div>

              {/* Raw Material Input Batch (Traceability) */}
              <div className={entryType !== 'PRODUCTION_ENTRY' ? 'hidden' : ''}>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5 flex items-center justify-between">
                  <span>Raw Material Batch</span>
                  <span className="text-[10px] text-indigo-600 font-normal">Optional</span>
                </label>
                <SearchableSelect
                  value={form.rawMaterialBatchNumber}
                  onChange={(val) => updateForm({ rawMaterialBatchNumber: val })}
                  options={entryRmBatchOptions}
                  placeholder="Select Raw Material Batch"
                  searchPlaceholder="Search batch..."
                />
                {availableRmBatches.length === 0 ? (
                  <p className="text-[11px] text-amber-600 mt-1">
                    No raw material batches found in system. Create a GRN first.
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Select raw material batch consumed for traceability genealogy.
                  </p>
                )}
              </div>

                </>
              )}

              {/* Common Fields: Date & Shift & Notes */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Date <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateForm({ date: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                />
              </div>

              {entryType !== 'MATERIAL_ISSUE' && (
                <>
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Shift <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={form.shiftId}
                  onChange={(val) => updateForm({ shiftId: val })}
                  options={entryShiftOptions}
                  placeholder="Select Shift"
                  searchPlaceholder="Search shift..."
                />
              </div>

              {/* Operator */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Operator <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={form.operatorId}
                  onChange={(val) => updateForm({ operatorId: val })}
                  options={entryOperatorOptions}
                  placeholder="Select Operator"
                  searchPlaceholder="Search operator..."
                />
              </div>

              {/* Machine */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Machine {entryType === 'DOWNTIME' && <span className="text-rose-500">*</span>}
                </label>
                <SearchableSelect
                  value={form.machineId}
                  onChange={(val) => updateForm({ machineId: val })}
                  options={entryMachineOptions}
                  disabled={entryType !== 'DOWNTIME' && !form.workOrderId}
                  placeholder={entryType === 'DOWNTIME' ? 'Select Machine' : form.workOrderId ? 'Select Machine (optional)' : 'Choose WO first'}
                  searchPlaceholder="Search machine..."
                />
              </div>

              {/* Downtime reason (from the Downtime Codes master) */}
              {entryType === 'DOWNTIME' && (
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                    Downtime Reason <span className="text-rose-500">*</span>
                  </label>
                  <SearchableSelect
                    value={form.downtimeCodeId}
                    onChange={(val) => updateForm({ downtimeCodeId: val })}
                    options={entryDowntimeCodeOptions}
                    placeholder="Select Downtime Reason"
                    searchPlaceholder="Search reason..."
                  />
                  {downtimeCodes.length === 0 && (
                    <p className="text-[11px] text-amber-600 mt-1">No downtime codes found. Add them in Masters → Downtime Codes.</p>
                  )}
                </div>
              )}

              {/* Rejection: quantity and reason */}
              {entryType === 'REJECTION' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                      Rejected Qty <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={form.rejectedQty}
                      onChange={(e) => updateForm({ rejectedQty: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                      Rejection Code <span className="text-rose-500">*</span>
                    </label>
                    <SearchableSelect
                      value={form.rejectionCodeId}
                      onChange={(val) => updateForm({ rejectionCodeId: val })}
                      options={entryRejectionCodeOptions}
                      placeholder="Select Rejection Code"
                      searchPlaceholder="Search code..."
                    />
                  </div>
                </div>
              )}

              {/* Times */}
              <div className={entryType === 'REJECTION' ? 'hidden' : 'grid grid-cols-2 gap-3'}>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                    Start Time <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={(e) => updateForm({ startTime: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                    End Time {entryType !== 'DOWNTIME' && <span className="text-rose-500">*</span>}
                  </label>
                  <input
                    type="time"
                    value={form.endTime}
                    onChange={(e) => updateForm({ endTime: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                  />
                </div>
              </div>
              {entryType !== 'REJECTION' && form.startTime && form.endTime && (
                <div className="text-xs text-[#64748b]">
                  Duration:{' '}
                  <span className="font-medium text-[#1e293b]">
                    {timeToMinutes(form.endTime) - timeToMinutes(form.startTime)} min
                  </span>
                </div>
              )}

              {/* Quantities */}
              {entryType === 'PRODUCTION_ENTRY' && materialLimit && (
                <div className={`px-3 py-2 rounded-lg border text-xs ${Number(form.producedQty || 0) + Number(form.rejectedQty || 0) > materialLimit.allowed
                  ? 'bg-rose-50 border-rose-200 text-rose-700'
                  : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                  <i className="ri-stack-line mr-1" />
                  {materialLimit.prevGood !== null
                    ? <>{materialLimit.prevStage} passed on <strong>{materialLimit.prevGood}</strong> unit(s) · <strong>{materialLimit.allowed}</strong> more can be recorded here.</>
                    : <>Material issued for this stage allows <strong>{materialLimit.max}</strong> unit(s) · <strong>{materialLimit.allowed}</strong> more at this stage.{materialLimit.limiting && ` Limiting material: ${materialLimit.limiting}.`}</>}
                </div>
              )}
              <div className={entryType !== 'PRODUCTION_ENTRY' ? 'hidden' : 'grid grid-cols-2 gap-3'}>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                    Produced Qty <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={form.producedQty}
                    onChange={(e) =>
                      updateForm({ producedQty: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                    Rejected Qty
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={form.rejectedQty}
                    onChange={(e) =>
                      updateForm({ rejectedQty: e.target.value })
                    }
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                  />
                </div>
              </div>

              {/* Rejection Code */}
              {entryType === 'PRODUCTION_ENTRY' && Number(form.rejectedQty || 0) > 0 && (
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                    Rejection Code <span className="text-rose-500">*</span>
                  </label>
                  <SearchableSelect
                    value={form.rejectionCodeId}
                    onChange={(val) => updateForm({ rejectionCodeId: val })}
                    options={entryRejectionCodeOptions}
                    placeholder="Select Rejection Code"
                    searchPlaceholder="Search code..."
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Notes
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => updateForm({ notes: e.target.value })}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 resize-none"
                />
              </div>
                </>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowSlideOver(false)}
                className="px-4 py-2 text-sm font-medium text-[#475569] border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-[#2563eb] rounded-lg hover:bg-blue-700 cursor-pointer"
              >
                {editingId ? 'Update Entry' : 'Save Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Slide-over */}
      {showRejectionSlideOver && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowRejectionSlideOver(false)}
          />
          <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-[#1e293b]">
                Log Rejection
              </h2>
              <button
                onClick={() => setShowRejectionSlideOver(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-5 space-y-5 flex-1">
              {rejectionError && (
                <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
                  {rejectionError}
                </div>
              )}

              {/* PO */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Production Order <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={rejectionForm.productionOrderId}
                  onChange={(val) =>
                    setRejectionForm((p) => ({
                      ...p,
                      productionOrderId: val,
                      workOrderId: '',
                    }))
                  }
                  options={rejPOOptions}
                  placeholder="Select Production Order"
                  searchPlaceholder="Search order..."
                />
              </div>

              {/* WO / Stage */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Work Order / Stage <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={rejectionForm.workOrderId}
                  onChange={(val) =>
                    setRejectionForm((p) => ({
                      ...p,
                      workOrderId: val,
                      machineId: '',
                    }))
                  }
                  options={rejWOOptions}
                  disabled={!rejectionForm.productionOrderId}
                  placeholder={rejectionForm.productionOrderId ? 'Select Work Order' : 'Choose PO first'}
                  searchPlaceholder="Search stage/WO..."
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Date <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={rejectionForm.date}
                  onChange={(e) =>
                    setRejectionForm((p) => ({ ...p, date: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                />
              </div>

              {/* Shift */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Shift <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={rejectionForm.shiftId}
                  onChange={(val) =>
                    setRejectionForm((p) => ({ ...p, shiftId: val }))
                  }
                  options={entryShiftOptions}
                  placeholder="Select Shift"
                  searchPlaceholder="Search shift..."
                />
              </div>

              {/* Operator */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Operator <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={rejectionForm.operatorId}
                  onChange={(val) =>
                    setRejectionForm((p) => ({
                      ...p,
                      operatorId: val,
                    }))
                  }
                  options={entryOperatorOptions}
                  placeholder="Select Operator"
                  searchPlaceholder="Search operator..."
                />
              </div>

              {/* Machine */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Machine
                </label>
                <SearchableSelect
                  value={rejectionForm.machineId}
                  onChange={(val) =>
                    setRejectionForm((p) => ({
                      ...p,
                      machineId: val,
                    }))
                  }
                  options={rejMachineOptions}
                  disabled={!rejectionForm.workOrderId}
                  placeholder={rejectionForm.workOrderId ? 'Select Machine (optional)' : 'Choose WO first'}
                  searchPlaceholder="Search machine..."
                />
              </div>

              {/* Rejection Code */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Rejection Code <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={rejectionForm.rejectionCodeId}
                  onChange={(val) =>
                    setRejectionForm((p) => ({
                      ...p,
                      rejectionCodeId: val,
                    }))
                  }
                  options={entryRejectionCodeOptions}
                  placeholder="Select Rejection Code"
                  searchPlaceholder="Search code..."
                />
                {selectedRejCode && (
                  <div className="mt-1.5">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border bg-rose-50 text-rose-700 border-rose-200">
                      {selectedRejCode.category}
                    </span>
                  </div>
                )}
              </div>

              {/* Rejected Qty + Unit */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                    Rejected Qty <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={rejectionForm.rejectedQty}
                    onChange={(e) =>
                      setRejectionForm((p) => ({
                        ...p,
                        rejectedQty: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={selectedPORej?.unit || 'Pcs'}
                    readOnly
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-500"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1.5">
                  Description
                </label>
                <textarea
                  value={rejectionForm.notes}
                  onChange={(e) =>
                    setRejectionForm((p) => ({ ...p, notes: e.target.value }))
                  }
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 resize-none"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowRejectionSlideOver(false)}
                className="px-4 py-2 text-sm font-medium text-[#475569] border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRejection}
                className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 cursor-pointer"
              >
                Log Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
